import React, { useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import { GridColDef, DataGridPro, GridValidRowModel } from '@mui/x-data-grid-pro';
import {
  Dialog,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@mui/material';

import useLocales from '../../../hooks/useLocales';
import { useSelector } from '../../../redux/store';
import useNotifications from '../../../hooks/useNotifications';
import documentService from '../../../services/documentService';
import { SelectedItemProp } from '../../documents/DocumentsList';
import { setCheckedItems } from '../../../redux/slices/document';
import { DLXDriveSelectedItem } from '../../../@types/dlx-drive/types';
import { EParakstsInitSignatureRequest } from '../../../@types/documents/types';
import { DocumentStatuses } from '../../../@types/documents/enums/documentStatuses';
import { TimeStamps, TimeStampPages } from '../../documents/signing/DocumentSigning';
import { initEParakstsSignatureSession } from '../../../redux/slices/signatureSession';
import { FOLDER_ACTIONS_MENU_ICON_SIZE } from '../../../utils/constants/dlxDriveConstants';
import { setDriveListIdState, addStartLoadingState } from '../../../redux/slices/dlxDrive';
import { SignatureFormatLevel } from '../../../@types/documents/enums/documentSignatareLevel';
import { DocumentSignatureType } from '../../../@types/documents/enums/documentSignatureTypes';

// ----------------------------------------------------------------

type DLXDriveBulkSignDialogProps = {
  isBulkSignDialogOpen: boolean;
  setIsBulkSignDialogOpen: (value: boolean) => void;
  isDocumentsListItems?: boolean;
  selectedIds?: SelectedItemProp[];
  handleRemoveItem?: (id: number) => void;
};

const FolderBulkSignDialog: React.FC<DLXDriveBulkSignDialogProps> = ({
  isBulkSignDialogOpen,
  setIsBulkSignDialogOpen,
  isDocumentsListItems,
  selectedIds,
  handleRemoveItem,
}) => {
  const { t, i18n } = useLocales();
  const { isLoading } = useSelector((state) => state.dlxDrive);
  const { checkedItems } = useSelector((state) => state.document);
  const { settings } = useSelector((state) => state.company);
  const [itemsToSign, setItemsToSign] = useState<GridValidRowModel[]>([]);
  const [itemsCantBeSigned, setItemsCantBeSigned] = useState<GridValidRowModel[]>([]);
  const { enqueueError } = useNotifications();

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'id',
      headerName: t('dlxDrive.dataGrid.columnId'),
    },
    {
      field: 'name',
      headerName: t('dlxDrive.dataGrid.columnName'),
      flex: 1,
    },
    {
      field: 'remove',
      headerName: t('dlxDrive.dataGrid.columnRemove'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const onClick = () => {
          const newItemsToApprove = itemsToSign.filter((item) => item.id !== params.row.id);

          setItemsToSign(newItemsToApprove);
        };

        return (
          <IconButton onClick={onClick} id="wG0ovZ">
            <img
              src="/icons/dlx-drive/actions/delete.svg"
              width={FOLDER_ACTIONS_MENU_ICON_SIZE}
              height={FOLDER_ACTIONS_MENU_ICON_SIZE}
              alt={t('delete')}
            />
          </IconButton>
        );
      },
    },
  ];

  const columnsCantBeSigned: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'id',
      headerName: t('dlxDrive.dataGrid.columnId'),
    },
    {
      field: 'name',
      headerName: t('dlxDrive.dataGrid.columnName'),
      flex: 1,
    },
  ];

  const rows = itemsToSign.map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const rowsCantBeSigned = itemsCantBeSigned.map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const handleClose = () => {
    setIsBulkSignDialogOpen(false);
  };

  const getItemType = (item: GridValidRowModel) => {
    let type: string;

    switch (item.name.split('.').pop()) {
      case 'adoc':
        type = 'adoc';
        break;
      case 'asice':
        type = 'asice';
        break;
      case 'bdoc':
        type = 'bdoc';
        break;
      case 'edoc':
        type = 'edoc';
        break;
      default:
        type = 'pdf';
        break;
    }

    return type;
  };

  const handleSign = async () => {
    addStartLoadingState();

    try {
      const documents = itemsToSign.map((item) => ({
        id: item.id,
        type: getItemType(item),
      }));

      const documentProps = itemsToSign.map((item) => ({
        id: item.id,
        highestSignatureLevel: item.highestSignatureLevel,
      }));

      const initSign = await documentService.getInitSignBulk(
        documentProps,
        DocumentSignatureType.EParaksts
      );

      if (!initSign || initSign?.isBlocked) {
        enqueueError('documents.snackbar.documentIsBeingSigned');
        return;
      }

      const timestampLogo = settings?.timestampImage || undefined;

      let originUrl = `${window.location.href}`;
      const paramsStartIndex = originUrl.indexOf('?');

      if (paramsStartIndex !== -1) {
        originUrl = originUrl.slice(0, paramsStartIndex);
      }

      const lastCharacter = originUrl.slice(-1);

      if (lastCharacter === '/') {
        originUrl = originUrl.slice(0, -1);
      }

      const eParakstsData = {
        signType: 1,
        fileExtension: 'pdf',
        documentId: undefined,
        personalCode: undefined,
        countryCode: undefined,
        documentIds: undefined,
        timestampPosition: TimeStamps.TopLeft,
        timestampImage: timestampLogo,
        companyCode: undefined,
        companyAddress: undefined,
        companyName: undefined,
        language: i18n.language,
        documentName: undefined,
        timestampPage: TimeStampPages.First,
        originUrl,
        signCoordinates: undefined,
        comment: undefined,
        documents,
      } as unknown as EParakstsInitSignatureRequest;

      await initEParakstsSignatureSession(eParakstsData);

      setCheckedItems([]);
    } catch (error) {
      console.error(error);
    } finally {
      setDriveListIdState();
      setIsBulkSignDialogOpen(false);
    }
  };

  useEffect(() => {
    if (isBulkSignDialogOpen && !isDocumentsListItems) {
      const newItemsToSign = checkedItems.filter(
        (item) =>
          item.status &&
          item.status === DocumentStatuses.WaitingForMe &&
          item.name.split('.').pop() !== 'adoc' &&
          item.highestSignatureLevel !== SignatureFormatLevel.LTA
      );

      setItemsToSign(newItemsToSign);

      const newItemsCantBeSigned = checkedItems.filter(
        (item) =>
          item.status === DocumentStatuses.WaitingForMe && item.name.split('.').pop() === 'adoc'
      );

      setItemsCantBeSigned(newItemsCantBeSigned);
    }

    if (selectedIds && isDocumentsListItems) {
      const newItemsToSign: DLXDriveSelectedItem[] = [];

      selectedIds.map((item) => {
        if (item.status === DocumentStatuses.WaitingForMe && item.extension !== 'adoc') {
          const newItem = {
            isFolder: item.isFolder,
            id: item.id,
            name: item.name,
            actions: [],
            status: item.status,
          } as DLXDriveSelectedItem;

          newItemsToSign.push(newItem);
        }
      });

      setItemsToSign(newItemsToSign);
    }
  }, [isBulkSignDialogOpen, checkedItems]);

  useEffect(() => {
    if (itemsToSign.length === 0 && itemsCantBeSigned.length === 0) {
      setIsBulkSignDialogOpen(false);
    }
  }, [itemsToSign]);

  return (
    <Dialog open={isBulkSignDialogOpen} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogTitle>{t('documents.bulkDocumentSigning')}</DialogTitle>

      <DialogContent>
        <DialogContentText>
          {t('documents.massApproving.messages.signDialogMessage')}
        </DialogContentText>

        <DataGridPro
          columns={columns}
          rows={rows}
          rowHeight={50}
          sx={{ width: '100%', mt: 2 }}
          disableColumnFilter
          disableColumnMenu
          disableMultipleColumnsSorting
          disableChildrenSorting
          localeText={{ footerTotalRows: t('dlxDrive.dataGrid.footerTotalRows') }}
        />

        {itemsCantBeSigned.length > 0 && (
          <>
            <DialogContentText>
              {t('documents.massApproving.messages.cantSignDialogMessage')}
            </DialogContentText>

            <DataGridPro
              columns={columnsCantBeSigned}
              rows={rowsCantBeSigned}
              rowHeight={50}
              rowSelection={false}
              sx={{
                width: '100%',
                mt: 2,
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                  bgcolor: 'rgba(255, 0, 0, 0.1)',
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  bgcolor: 'rgba(255, 0, 0, 0.1)',
                },
                '.MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '.MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
              }}
              disableColumnFilter
              disableColumnMenu
              disableMultipleColumnsSorting
              disableChildrenSorting
              localeText={{ footerTotalRows: t('dlxDrive.dataGrid.footerTotalRows') }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'left', textAlign: 'center', gap: 1 }}>
        <LoadingButton loading={isLoading} variant="outlined" onClick={handleClose} id="mz5nnxqa">
          {`${t('shared.buttons.cancel')}`}
        </LoadingButton>

        <LoadingButton
          disabled={itemsToSign.length === 0}
          loading={isLoading}
          variant="contained"
          onClick={handleSign}
          id="yd0sown5"
        >
          {`${t('shared.buttons.sign')}`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FolderBulkSignDialog;
